import { defineStore } from "pinia";
import { ref, Ref } from "vue";
import {
  INewWorkType,
  IUpdateWorkType,
  IWorkType,
  IWorkTypeFilter,
} from "@/features/work-types/work-type.types";
import { ITableHeader } from "@/types/table";
import { workTypeService } from "./work-type.service";
import { useNotification } from "@/utils/notification";

const { pushNotification } = useNotification();

export const useWorkTypeStore = defineStore("workType", () => {
  const workTypesForFilter: IWorkType[] = [];
  const workTypeHeaders: ITableHeader[] = [
    {
      name: "id",
      label: "No",
      fitContent: true,
    },
    {
      name: "title",
      label: "Name",
      width: "20%",
    },
    {
      name: "description",
      label: "Description",
      width: "20%",
    },
    {
      name: "base_points",
      label: "Base Points",
      width: "10%",
    },
    {
      name: "complete_minutes",
      label: "Complete Minutes",
      width: "15%",
    },
    {
      name: "priority",
      label: "Priority",
      width: "10%",
    },
    {
      name: "status",
      label: "Status",
      width: "10%",
    },
    {
      name: "actions",
      label: "Actions",
      width: "10%",
    },
  ];

  const commonFilter = ref<IWorkTypeFilter>({
    is_active: true,
    search: "",
  });

  const clearFilter = () => {
    commonFilter.value = {
      is_active: true,
      search: "",
    };
  };

  async function getWorkTypes(loading: Ref<boolean>): Promise<IWorkType[]> {
    try {
      loading.value = true;
      const { data } = await workTypeService.getWorkTypes({
        ...commonFilter.value,
      });
      return data;
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      pushNotification(message, "error");
      return [] as any;
    } finally {
      loading.value = false;
    }
  }

  async function createWorkType(
    loading: Ref<boolean>,
    payload: INewWorkType,
    cl: any
  ) {
    loading.value = true;

    const newResult = { ...payload };
    newResult.code = payload.name.toLowerCase().split(" ").join("_");

    await workTypeService
      .createWorkType(newResult)
      .then((result) => {
        cl(result.data);
      })
      .catch((err) => {
        pushNotification(err.response?.data?.detail || err, "error");
      })
      .finally(() => {
        loading.value = false;
      });
  }

  async function getWorkTypeById(
    loading: Ref<boolean>,
    id: string
  ): Promise<IWorkType> {
    try {
      loading.value = true;
      const { data } = await workTypeService.getWorkTypeById(id);
      return data;
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      pushNotification(message, "error");
      return {} as any;
    } finally {
      loading.value = false;
    }
  }

  async function updateWorkType(
    loading: Ref<boolean>,
    payload: IUpdateWorkType,
    id: string
  ) {
    try {
      loading.value = true;
      const { data } = await workTypeService.updateWorkType(id, payload);
      return data;
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      pushNotification(message, "error");
      return {} as any;
    } finally {
      loading.value = false;
    }
  }

  async function seedDefaultWorkType(loading: Ref<boolean>) {
    try {
      loading.value = true;
      const { data } = await workTypeService.seedDefaultWorkType();
      return data;
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      pushNotification(message, "error");
      return {} as any;
    } finally {
      loading.value = false;
    }
  }

  return {
    commonFilter,
    workTypesForFilter,
    workTypeHeaders,
    clearFilter,
    getWorkTypes,
    createWorkType,
    getWorkTypeById,
    updateWorkType,
    seedDefaultWorkType,
  };
});
